import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { provideClientHydration, withNoIncrementalHydration } from '@angular/platform-browser';
import { provideFileRouter, requestContextInterceptor } from '@analogjs/router';
import { TitleStrategy } from '@angular/router';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { authInterceptor } from '@mb/auth/client';
import { GlobalErrorHandler } from '@mb/common/client';

import { isWorkforceSentryEnabled } from '../config/sentry-env';
import { provideAppI18n } from './providers/app-i18n.providers';
import { TranslatedTitleStrategy } from './providers/translated-title.strategy';
import { provideWorkforceAuth } from './providers/auth.providers';
import { provideOrganizationContext } from './providers/organization-context.providers';
import { provideTableColumnStore } from './providers/table-columns.providers';

/** Angular integration (after `Sentry.init` in main.ts). */
const sentryProviders = isWorkforceSentryEnabled()
  ? [
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        inject(Sentry.TraceService);
      }),
    ]
  : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideFileRouter(),
    provideClientHydration(withNoIncrementalHydration()),
    provideHttpClient(
      withFetch(),
      withInterceptors([requestContextInterceptor, authInterceptor]),
    ),
    provideAppI18n(),
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    ...provideWorkforceAuth(),
    ...provideOrganizationContext(),
    ...provideTableColumnStore(),
    // Custom global error handler — always registered, forwards to Sentry via clientLogger.
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    ...sentryProviders,
  ],
};
